// Project Detail — Sub-contractors register. Each entry links a company to a
// contact email and the type of work they carry out. Autosaves the full list.
import { useState } from "react";
import { HardHat, Plus, Trash2, Loader2, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SectionCard } from "@/components/SectionCard";

export default function ProjectSubcontractorsPanel({ project, canEdit, onChanged }) {
    const [rows, setRows] = useState(() => (project.subcontractors || []).map((s) => ({ ...s })));
    const [busy, setBusy] = useState(false);
    const [dirty, setDirty] = useState(false);

    const update = (i, patch) => { setRows((r) => r.map((x, idx) => (idx === i ? { ...x, ...patch } : x))); setDirty(true); };
    const add = () => { setRows((r) => [...r, { company: "", email: "", work_type: "" }]); setDirty(true); };
    const remove = (i) => { setRows((r) => r.filter((_, idx) => idx !== i)); setDirty(true); };

    async function save() {
        const clean = rows.filter((r) => (r.company || "").trim());
        setBusy(true);
        try {
            const { data } = await api.put(`/projects/${project.id}/subcontractors`, { subcontractors: clean });
            setRows((data.subcontractors || []).map((s) => ({ ...s })));
            setDirty(false);
            toast.success("Sub-contractors saved");
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
        finally { setBusy(false); }
    }

    return (
        <SectionCard testId="project-subcontractors-panel" id={`subs-${project.id}`} icon={HardHat}
            title="Sub-contractors" count={rows.length}
            actions={canEdit && (
                    <div className="flex items-center gap-2">
                        <button data-testid="subcontractor-add" onClick={add}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-[8px] text-[11.5px] font-semibold border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA", color: "#2D2D2D" }}>
                            <Plus className="w-3.5 h-3.5" /> Add
                        </button>
                        {dirty && (
                            <button data-testid="subcontractor-save" onClick={save} disabled={busy}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-[8px] text-[11.5px] font-semibold text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
                            </button>
                        )}
                    </div>
            )}>

            {rows.length === 0 ? (
                <div className="py-5 text-center text-[12.5px] italic" style={{ color: "#9A968F" }}>No sub-contractors recorded.{canEdit ? " Add one above." : ""}</div>
            ) : (
                <div className="space-y-2">
                    {rows.map((r, i) => (
                        <div key={r.id || i} data-testid={`subcontractor-row-${i}`} className="grid grid-cols-1 sm:grid-cols-[1.2fr_1.4fr_1fr_auto] gap-2 items-center rounded-[10px] border p-2" style={{ borderColor: "#EFEAE3", background: "#FCFBF9" }}>
                            <input data-testid={`subcontractor-company-${i}`} value={r.company || ""} disabled={!canEdit} onChange={(e) => update(i, { company: e.target.value })}
                                placeholder="Company" className="px-2.5 py-1.5 border rounded-lg text-[12px] outline-none bg-white" style={{ borderColor: "#E5E0DA" }} />
                            <input data-testid={`subcontractor-email-${i}`} value={r.email || ""} disabled={!canEdit} onChange={(e) => update(i, { email: e.target.value })}
                                placeholder="Email" className="px-2.5 py-1.5 border rounded-lg text-[12px] outline-none bg-white font-mono" style={{ borderColor: "#E5E0DA" }} />
                            <input data-testid={`subcontractor-work-${i}`} value={r.work_type || ""} disabled={!canEdit} onChange={(e) => update(i, { work_type: e.target.value })}
                                placeholder="Work type" className="px-2.5 py-1.5 border rounded-lg text-[12px] outline-none bg-white" style={{ borderColor: "#E5E0DA" }} />
                            {canEdit && (
                                <button data-testid={`subcontractor-remove-${i}`} onClick={() => remove(i)} className="w-8 h-8 grid place-items-center rounded hover:bg-[#FBE8E5]" style={{ color: "#B33A2E" }}><Trash2 className="w-4 h-4" /></button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </SectionCard>
    );
}
